import { Heart } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'

interface Donor {
  id: number
  display_name: string
  avatar_url: string | null
  level: number
  total_donated: number
  donation_count: number
  last_donation_at: string
  message: string | null
}

const GRADIENTS = [
  'linear-gradient(135deg, #10b981, #059669)',
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f59e0b, #d97706)',
  'linear-gradient(135deg, #ec4899, #db2777)',
  'linear-gradient(135deg, #3b82f6, #2563eb)',
]

export default function ProjectDonors({ projectId }: { projectId: number }) {
  const [donors, setDonors] = useState<Donor[]>([])
  const [totalDonated, setTotalDonated] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}/donors`)
        if (res.ok) {
          const data = await res.json() as { donors: Donor[]; total_donated: number }
          setDonors(data.donors || [])
          setTotalDonated(data.total_donated || 0)
        }
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [projectId])

  if (loading) {return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>}

  if (donors.length === 0) {return (
    <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
      <Heart size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
      <p style={{ margin: 0 }}>No donations yet</p>
      <p style={{ fontSize: 13, marginTop: 6, opacity: 0.7 }}>Be the first to support this research</p>
    </div>
  )}

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <Heart size={18} style={{ color: '#ef4444' }} />
        <span style={{ fontWeight: 600, fontSize: 16 }}>Donors</span>
        <span style={{
          background: 'var(--border-color)', color: 'var(--text-secondary)',
          borderRadius: 20, padding: '1px 8px', fontSize: 12
        }}>{donors.length}</span>
      </div>

      {/* Total */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(99,102,241,0.05))',
        border: '1px solid rgba(139,92,246,0.2)',
        borderRadius: 14, padding: '20px 24px', marginBottom: 24, textAlign: 'center'
      }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: '#8b5cf6' }}>
          {totalDonated.toLocaleString()} FLZ
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
          Total donated by {donors.length} supporter{donors.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {donors.map((donor, i) => (
          <div key={donor.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: 14,
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: 12, padding: '14px 16px'
          }}>
            {/* Avatar */}
            <div style={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: GRADIENTS[i % GRADIENTS.length],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 18
            }}>
              {donor.display_name.charAt(0).toUpperCase()}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>
                    {donor.display_name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    {donor.donation_count} donation{donor.donation_count !== 1 ? 's' : ''} · Lv {donor.level || 1} ·{' '}
                    {new Date(donor.last_donation_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#8b5cf6', whiteSpace: 'nowrap' }}>
                  {donor.total_donated.toLocaleString()} FLZ
                </div>
              </div>
              {donor.message && (
                <p style={{
                  margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)',
                  lineHeight: 1.5, fontStyle: 'italic'
                }}>
                  &ldquo;{donor.message}&rdquo;
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
